import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { WAIcon, ADMIN_WA } from './academyUtils';
import AnnouncementBanner from '../AnnouncementBanner';

const WA_ADMIN = ADMIN_WA;

// ── Static data (primary source — works without DB) ────────────────────────
const CAT_META = {
  'General English':       { icon:'🇬🇧', color:'#1a5fa8', bg:'#e8f0fb' },
  'IELTS':                 { icon:'📝', color:'#b8620a', bg:'#fff8f0' },
  'Speaking & Fluency':    { icon:'🗣️', color:'#1a7a4a', bg:'#e8f5ee' },
  'SAT English':           { icon:'📐', color:'#5a1a8a', bg:'#f5ebff' },
  'SAT Math':              { icon:'🔢', color:'#5a1a8a', bg:'#f5ebff' },
  'German':                { icon:'🇩🇪', color:'#c0392b', bg:'#fff0f0' },
  'Interview Preparation': { icon:'💼', color:'#0a6a7a', bg:'#e8f8fb' },
  'Speaking Exam':         { icon:'🎯', color:'#e67e22', bg:'#fff8f0' },
};

const STATIC_TEACHERS = [
  { id:'t1',  name:'Mr. Mahmoud El Sayed',       cats:['General English'] },
  { id:'t2',  name:'Ms. Asmaa',                  cats:['General English'] },
  { id:'t3',  name:'Ms. Mariam',                 cats:['General English'] },
  { id:'t4',  name:'Ms. Nadine',                 cats:['General English','Speaking & Fluency'],
    badge:'🇬🇧 Native British', badgeColor:'#1a7a4a', badgeBg:'#e8f5ee' },
  { id:'t5',  name:'Ms. Mimi',                   cats:['General English'] },
  { id:'t6',  name:'Ms. Arwa Abdeltawab',        cats:['General English'] },
  { id:'t7',  name:'Ms. Khulood',                cats:['General English'] },
  { id:'t8',  name:'Ms. Malak',                  cats:['General English'] },
  { id:'t9',  name:'Ms. Mona Elsayed',           cats:['IELTS','General English'] },
  { id:'t10', name:'Mr. Mahmoud Said',           cats:['General English'] },
  { id:'t11', name:'Ms. Nancy',                  cats:['General English','IELTS'] },
  { id:'t12', name:'Ms. Habiba',                 cats:['Speaking & Fluency','IELTS','General English','Speaking Exam'],
    badge:'✓ Certified Examiner', badgeColor:'#b8620a', badgeBg:'#fff3e0', examiner:true },
  { id:'t13', name:'Ms. Nirvana',                cats:['General English'] },
  { id:'t14', name:'Mr. Ahmed (SAT Math)',        cats:['SAT Math'] },
  { id:'t15', name:'Mr. Mohamed Saad (SAT Eng)', cats:['SAT English'] },
  { id:'t16', name:'Ms. Silvia (SAT English)',   cats:['SAT English'] },
  { id:'t17', name:'Frau. Asmaa (German)',       cats:['German'] },
  { id:'t18', name:'Ms. Esraa N',               cats:['General English'] },
];

// Count teachers per category from static data
const catTeacherCount = (catName) =>
  STATIC_TEACHERS.filter(t => t.cats.includes(catName)).length;

const CATEGORY_CARDS = [
  { name:'General English',       ...CAT_META['General English'] },
  { name:'IELTS',                 ...CAT_META['IELTS'],           displayName:'IELTS Preparation' },
  { name:'Speaking & Fluency',    ...CAT_META['Speaking & Fluency'] },
  { name:'SAT English',           ...CAT_META['SAT English'] },
  { name:'SAT Math',              ...CAT_META['SAT Math'] },
  { name:'German',                ...CAT_META['German'] },
  { name:'Interview Preparation', ...CAT_META['Interview Preparation'] },
  { name:'Speaking Exam',         ...CAT_META['Speaking Exam'],  displayName:'Speaking Mock Exam' },
];

// Initials from name
function initials(name) {
  return name.split(' ').slice(0,2).map(w=>w[0]?.toUpperCase()||'').join('');
}

// Avatar background colours cycle
const AVATAR_COLORS = ['#1a2a4a','#1a5fa8','#1a7a4a','#b8620a','#5a1a8a','#0a6a7a','#c0392b','#e67e22'];
function avatarBg(idx) { return AVATAR_COLORS[idx % AVATAR_COLORS.length]; }

// ── Packages ──────────────────────���────────────────────────────��────────────
const IELTS_PACKAGES = [
  {
    name:'Standard Package', price:'500 AED', was:null, color:'#1a5fa8', bg:'#e8f0fb',
    features:[
      '1 complete IELTS preparation round (6 weeks)',
      'All 4 skills: Reading, Listening, Writing, Speaking',
      '12 Writing assessments — feedback from our certified IELTS coaches',
      '12 Speaking mock exams with certified IELTS examiner',
      'AI-powered Writing Task 2 feedback after every session',
      'Full mock test with examiner feedback',
      'WhatsApp group support throughout',
    ],
  },
  {
    name:'Premium Package', price:'800 AED', was:'1800 AED', color:'#b8860b', bg:'#fffaf0',
    badge:'Best Value', tagline:'We stay with you until you reach your target score.',
    features:[
      'Unlimited 6-week rounds — repeat as many times as needed',
      'Each round is a complete fresh programme: new topics, new passages, new writing questions, new speaking questions',
      '12 Writing assessments per round — feedback from our certified IELTS coaches',
      '12 Speaking mock exams per round with certified IELTS examiner',
      'AI-powered Writing Task 2 feedback after every writing session',
      'Full mock test at end of every round with examiner feedback',
      'WhatsApp group support throughout your entire journey',
      'One payment. Unlimited rounds. Your score — guaranteed.',
    ],
  },
];

// ── Component ─────────────────────────────────────────────────────��─────────
export default function AcademyHome({ onBook, onStudentLogin, onRegister, onTeacherLogin, onPlacementTest, onBack }) {
  const [activeCat, setActiveCat] = useState('all');
  const teachersRef = useRef(null);

  function openWA(msg) {
    window.open(`https://wa.me/${WA_ADMIN}?text=${encodeURIComponent(msg)}`, '_blank');
  }

  function handleCatClick(catName) {
    setActiveCat(catName === activeCat ? 'all' : catName);
    setTimeout(() => teachersRef.current?.scrollIntoView({ behavior:'smooth', block:'start' }), 50);
  }

  return (
    <div style={{ minHeight:'100vh', background:'#f0f4f8', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>

      {/* ── HERO ── */}
      <div style={{ background:'linear-gradient(135deg,#1a2a4a 0%,#2a3a6a 60%,#1a2a4a 100%)', padding:'52px 24px 44px', textAlign:'center', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, background:'radial-gradient(ellipse at 50% 0%,rgba(184,134,11,0.18) 0%,transparent 60%)', pointerEvents:'none' }}/>
        <button onClick={onBack} style={{ position:'absolute', top:16, left:16, background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)', color:'rgba(255,255,255,0.8)', cursor:'pointer', fontSize:12, fontFamily:"'Plus Jakarta Sans',sans-serif", borderRadius:7, padding:'6px 13px' }}>← Back</button>
        <div style={{ position:'absolute', top:14, right:16, background:'rgba(255,255,255,0.12)', borderRadius:8, padding:'4px 8px', display:'flex', alignItems:'center' }}>
          <img src="/logo.png" alt="IELTS Compass" style={{ height:28, objectFit:'contain' }}/>
        </div>
        <div style={{ position:'relative', maxWidth:640, margin:'0 auto' }}>
          <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.2em', textTransform:'uppercase', color:'#b8860b', marginBottom:12 }}>IELTS COMPASS · Compass Academy</div>
          <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'clamp(26px,4.5vw,46px)', fontWeight:700, color:'#fff', lineHeight:1.2, marginBottom:12 }}>
            Expert English &amp; Language Coaching
          </h1>
          <p style={{ fontSize:14, color:'rgba(255,255,255,0.7)', lineHeight:1.7, marginBottom:28, maxWidth:500, margin:'0 auto 28px' }}>
            Book one-to-one or group lessons with our specialist teachers. 18 teachers across General English, IELTS, SAT, German, Speaking, and more.
          </p>
          <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
            <button onClick={() => openWA('Hello Mr. Ahmed Salem! I would like to start lessons at Compass Academy. Please help me get matched with a teacher.')}
              style={{ background:'#25d366', color:'#fff', border:'none', padding:'13px 28px', borderRadius:9, fontSize:15, fontWeight:700, cursor:'pointer', boxShadow:'0 4px 14px rgba(37,211,102,0.35)', display:'flex', alignItems:'center', gap:8 }}>
              <WAIcon size={17}/> Contact Mr. Ahmed
            </button>
            <button onClick={onPlacementTest}
              style={{ background:'rgba(255,255,255,0.12)', color:'#fff', border:'1.5px solid rgba(255,255,255,0.3)', padding:'13px 28px', borderRadius:9, fontSize:15, fontWeight:600, cursor:'pointer' }}>
              📝 Take Placement Test
            </button>
          </div>
        </div>
      </div>

      {/* Announcement banner — academy context */}
      <AnnouncementBanner/>

      <div style={{ maxWidth:980, margin:'0 auto', padding:'36px 16px' }}>

        {/* ── OUR COURSES & PROGRAMMES ── */}
        <div style={{ marginBottom:40 }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, fontWeight:700, color:'#1a2a4a', marginBottom:8 }}>Our Courses &amp; Programmes</div>
          <p style={{ fontSize:13.5, color:'#5a6a88', marginBottom:20, lineHeight:1.75, maxWidth:640 }}>
            Whether you are a complete beginner or preparing for IELTS, we have the right programme for you. Take our free placement test to find your level, or contact Mr. Ahmed Salem to discuss which course suits you best.
          </p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(148px,1fr))', gap:12 }}>
            {CATEGORY_CARDS.map(c => {
              const count = catTeacherCount(c.name);
              const active = activeCat === c.name;
              return (
                <button key={c.name} onClick={() => { handleCatClick(c.name); openWA(`Hello Mr. Ahmed Salem! I am interested in ${c.displayName||c.name} lessons. Please help me get started.`); }}
                  style={{
                    background: active ? c.bg : '#fff',
                    border: `2px solid ${active ? c.color : c.color+'33'}`,
                    borderRadius:14, padding:'18px 12px', cursor:'pointer', textAlign:'center',
                    transition:'all .18s', boxShadow: active ? `0 4px 18px ${c.color}30` : 'none',
                    transform: active ? 'translateY(-3px)' : 'none',
                  }}
                  onMouseEnter={e=>{ if(!active){e.currentTarget.style.boxShadow=`0 3px 12px ${c.color}20`;e.currentTarget.style.transform='translateY(-2px)';} }}
                  onMouseLeave={e=>{ if(!active){e.currentTarget.style.boxShadow='none';e.currentTarget.style.transform='none';} }}>
                  <div style={{ fontSize:30, marginBottom:8 }}>{c.icon}</div>
                  <div style={{ fontSize:13, fontWeight:700, color:c.color, marginBottom:4, lineHeight:1.3 }}>{c.displayName||c.name}</div>
                  {count > 0 && (
                    <div style={{ fontSize:11, color:'#9aa3b8' }}>{count} teacher{count!==1?'s':''}</div>
                  )}
                  <div style={{ marginTop:10, fontSize:11.5, fontWeight:600, color:c.color, background:`${c.color}18`, border:`1px solid ${c.color}33`, borderRadius:100, padding:'3px 10px', display:'inline-block' }}>
                    Browse Teachers
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── TWO PROGRAMME SECTIONS ── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:18, marginBottom:40 }}>
          {/* General English */}
          <div style={{ background:'#fff', border:'2px solid #c0d8f8', borderRadius:16, padding:24, boxShadow:'0 2px 12px rgba(26,42,74,0.07)' }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:11, fontWeight:700, color:'#1a5fa8', background:'#e8f0fb', border:'1px solid #c0d8f8', borderRadius:100, padding:'3px 12px', marginBottom:14, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.08em' }}>
              🇬🇧 GENERAL ENGLISH
            </div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>General English Courses</div>
            <div style={{ fontSize:13, color:'#1a5fa8', fontWeight:600, marginBottom:12 }}>A1 to C1 · All levels welcome</div>
            <div style={{ fontSize:13, color:'#5a6a88', marginBottom:16, lineHeight:1.7 }}>
              Flexible private and group lessons with expert teachers. Our team of 18 specialists covers all proficiency levels, from absolute beginners to advanced learners.
            </div>
            {['Private 1-on-1 lessons', 'Small group classes (2–10 students)', 'Conversation & fluency practice', 'Grammar, writing, and reading skills', 'Interview and business English'].map((f,i)=>(
              <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-start', marginBottom:6, fontSize:13, color:'#2a3a5a', lineHeight:1.5 }}>
                <span style={{ color:'#1a5fa8', flexShrink:0 }}>✓</span>{f}
              </div>
            ))}
            <button onClick={() => openWA('Hello Mr. Ahmed Salem! I am interested in General English lessons. Please help me get started.')}
              style={{ width:'100%', marginTop:18, background:'#25d366', color:'#fff', border:'none', padding:'12px 0', borderRadius:10, fontSize:13.5, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
              <WAIcon size={15}/> Book a Trial Lesson
            </button>
          </div>

          {/* IELTS */}
          <div style={{ background:'linear-gradient(135deg,#1a2a4a 0%,#2a3a6a 100%)', border:'none', borderRadius:16, padding:24, boxShadow:'0 4px 18px rgba(26,42,74,0.18)', color:'#fff' }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:11, fontWeight:700, color:'#d4a017', background:'rgba(212,160,23,0.15)', border:'1px solid rgba(212,160,23,0.35)', borderRadius:100, padding:'3px 12px', marginBottom:14, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.08em' }}>
              📝 IELTS PREPARATION
            </div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:'#fff', marginBottom:4 }}>IELTS Preparation</div>
            <div style={{ fontSize:13, color:'#d4a017', fontWeight:600, marginBottom:12 }}>with Mr. Ahmed Salem</div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.75)', marginBottom:16, lineHeight:1.7 }}>
              Structured 6-week programme with AI-powered writing feedback and mock speaking exams. Proven results across hundreds of students.
            </div>
            {['Structured 6-week preparation rounds', 'AI-powered Writing Task 2 feedback', '12 Speaking mock exams with certified examiner', 'Reading & Listening full mock tests', 'WhatsApp group support throughout'].map((f,i)=>(
              <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-start', marginBottom:6, fontSize:13, color:'rgba(255,255,255,0.85)', lineHeight:1.5 }}>
                <span style={{ color:'#d4a017', flexShrink:0 }}>✓</span>{f}
              </div>
            ))}
            <button onClick={() => openWA('Hello Mr. Ahmed Salem! I am interested in IELTS preparation. Please contact me with details about the programme.')}
              style={{ width:'100%', marginTop:18, background:'linear-gradient(135deg,#b8860b,#d4a017)', color:'#fff', border:'none', padding:'12px 0', borderRadius:10, fontSize:13.5, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, boxShadow:'0 3px 12px rgba(184,134,11,0.35)' }}>
              <WAIcon size={15}/> Book a Trial Lesson
            </button>
          </div>
        </div>

        {/* ── HOW IT WORKS ── */}
        <div ref={teachersRef} style={{ marginBottom:44 }}>
          <div style={{ background:'linear-gradient(135deg,#1a2a4a,#2a3a6a)', borderRadius:16, padding:'28px 28px 24px', color:'#fff' }}>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, fontWeight:700, marginBottom:6 }}>We match you with the right teacher</div>
            <div style={{ fontSize:14, color:'rgba(255,255,255,0.75)', lineHeight:1.75, marginBottom:20, maxWidth:560 }}>
              After your placement test, Mr. Ahmed Salem will personally review your level, goals, and schedule — then match you with the most suitable teacher from our team of 18 specialists.
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))', gap:12, marginBottom:24 }}>
              {[
                { n:'1', icon:'📝', text:'Take the free Placement Test' },
                { n:'2', icon:'💬', text:'Mr. Ahmed reviews your level and goals' },
                { n:'3', icon:'👩‍🏫', text:'Get matched with your ideal teacher' },
                { n:'4', icon:'📅', text:'Book lessons through your personal WhatsApp group' },
              ].map(step => (
                <div key={step.n} style={{ background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:10, padding:'14px 14px' }}>
                  <div style={{ fontSize:20, marginBottom:6 }}>{step.icon}</div>
                  <div style={{ fontSize:13, color:'rgba(255,255,255,0.85)', lineHeight:1.5 }}>{step.text}</div>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
              <button onClick={() => openWA('Hello Mr. Ahmed Salem! I would like to start lessons at Compass Academy. Please help me get matched with a teacher.')}
                style={{ background:'#25d366', color:'#fff', border:'none', padding:'13px 24px', borderRadius:9, fontSize:14, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:8, boxShadow:'0 3px 12px rgba(37,211,102,0.35)' }}>
                <WAIcon size={16}/> Contact Mr. Ahmed to Get Started
              </button>
              <button onClick={onPlacementTest}
                style={{ background:'rgba(255,255,255,0.12)', color:'#fff', border:'1.5px solid rgba(255,255,255,0.25)', padding:'13px 24px', borderRadius:9, fontSize:14, fontWeight:600, cursor:'pointer' }}>
                📝 Take Placement Test First
              </button>
            </div>
          </div>
        </div>


        {/* ── IELTS PACKAGES ── */}
        <div style={{ marginBottom:44 }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>IELTS Preparation Packages</div>
          <div style={{ fontSize:13.5, color:'#5a6a88', marginBottom:20 }}>with Mr. Ahmed Salem · Monday–Thursday 8:00–10:00 PM (UAE)</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:18 }}>
            {IELTS_PACKAGES.map(pkg => (
              <div key={pkg.name} style={{ background:'#fff', border:`2px solid ${pkg.color}44`, borderRadius:16, padding:26, boxShadow:'0 3px 16px rgba(26,42,74,0.08)', display:'flex', flexDirection:'column' }}>
                {pkg.badge && (
                  <div style={{ fontSize:11, fontWeight:700, color:pkg.color, background:`${pkg.color}18`, border:`1px solid ${pkg.color}44`, borderRadius:100, padding:'3px 12px', marginBottom:10, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.08em', display:'inline-block', alignSelf:'flex-start' }}>{pkg.badge}</div>
                )}
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:700, color:'#1a2a4a', marginBottom:2 }}>{pkg.name}</div>
                {pkg.tagline && <div style={{ fontSize:13, color:pkg.color, fontStyle:'italic', marginBottom:8, fontWeight:600 }}>{pkg.tagline}</div>}
                <div style={{ marginBottom:14 }}>
                  <span style={{ fontSize:14, color:pkg.color, fontStyle:'italic', fontWeight:600 }}>Contact for pricing</span>
                </div>
                <ul style={{ listStyle:'none', margin:'0 0 20px', padding:0, flex:1 }}>
                  {pkg.features.map((f, i) => (
                    <li key={i} style={{ display:'flex', gap:8, alignItems:'flex-start', marginBottom:7, fontSize:13, color:'#2a3a5a', lineHeight:1.55 }}>
                      <span style={{ color:pkg.color, flexShrink:0, marginTop:1 }}>✓</span>{f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => openWA(`Hello Mr. Ahmed Salem! I am interested in the IELTS ${pkg.name}. Please contact me with enrolment details.`)}
                  style={{ width:'100%', background:'#25d366', color:'#fff', border:'none', padding:'13px 0', borderRadius:9, fontSize:14, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:9, boxShadow:'0 2px 10px rgba(37,211,102,0.3)' }}>
                  <WAIcon size={16}/> Enquire on WhatsApp
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* ── STUDENT SUCCESS STORIES ── */}
        <div style={{ marginBottom:44 }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>Our Students' Results 🏆</div>
          <div style={{ fontSize:13.5, color:'#5a6a88', marginBottom:22 }}>Real students. Real scores. Real success.</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:18 }}>
            {[
              { name:'Sara Mohamed',      flag:'🇪🇬', country:'Egypt', band:'7.0', prev:'5.5', course:'IELTS Premium',   quote:'"The structured approach and weekly feedback completely transformed my writing. I got my target score in just 3 months."' },
              { name:'Omar Khalil',        flag:'🇦🇪', country:'UAE',   band:'7.5', prev:'5.5', course:'IELTS Premium',   quote:'"Mr. Ahmed\'s method of breaking down each paragraph sentence by sentence made everything click. Highly recommended."' },
              { name:'Fatima Al Rashidi', flag:'🇦🇪', country:'UAE',   band:'6.5', prev:'5.0', course:'IELTS Standard',  quote:'"I had failed IELTS twice before. The AI feedback showed me exactly what I was doing wrong. Third time — success!"' },
            ].map((s,i)=>(
              <div key={i} style={{ background:'#fff', borderTop:`3px solid #b8860b`, borderRadius:14, padding:22, boxShadow:'0 2px 12px rgba(26,42,74,0.08)', display:'flex', flexDirection:'column' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14, color:'#1a2233' }}>{s.flag} {s.name}</div>
                    <div style={{ fontSize:12, color:'#9aa3b8' }}>{s.country}</div>
                  </div>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, fontWeight:700, color:'#b8860b', lineHeight:1 }}>Band {s.band}</div>
                </div>
                <div style={{ fontSize:12, color:'#5a6a88', marginBottom:10, background:'#f8f9fb', borderRadius:8, padding:'7px 10px' }}>
                  Started at Band {s.prev} → Achieved Band {s.band}
                </div>
                <div style={{ fontSize:13, color:'#2a3a5a', fontStyle:'italic', lineHeight:1.65, flex:1, marginBottom:12 }}>{s.quote}</div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span style={{ fontSize:11, fontWeight:600, color:'#b8860b', background:'#fffaf0', border:'1px solid #f0d890', borderRadius:100, padding:'2px 9px', fontFamily:"'JetBrains Mono',monospace" }}>{s.course}</span>
                  <span style={{ fontSize:14, color:'#b8860b', letterSpacing:1 }}>★★★★★</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── SPEAKING MOCK EXAM ── */}
        <div style={{ marginBottom:44 }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>🎯 Book an IELTS Speaking Mock Exam</div>
          <div style={{ fontSize:13.5, color:'#5a6a88', marginBottom:20 }}>
            Practice your IELTS Speaking test with a certified examiner. Receive a full examiner report with band scores and feedback sent directly to your WhatsApp.
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:16 }}>
            {/* Card 1: For IELTS Students */}
            <div style={{ background:'#fff', border:'1.5px solid #c0d8f8', borderRadius:14, padding:22 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#1a5fa8', background:'#e8f0fb', border:'1px solid #c0d8f8', borderRadius:100, padding:'2px 10px', display:'inline-block', marginBottom:10, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.08em' }}>For IELTS Students</div>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:19, fontWeight:700, color:'#1a2a4a', marginBottom:10 }}>Included in your package</div>
              {[
                '12 exams per round (Standard)',
                'Unlimited exams per round (Premium)',
                'Book through your WhatsApp group',
                'Full examiner report with band scores',
              ].map((f,i)=>(
                <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-start', marginBottom:6, fontSize:13, color:'#2a3a5a', lineHeight:1.55 }}>
                  <span style={{ color:'#1a5fa8', flexShrink:0 }}>✓</span>{f}
                </div>
              ))}
              <div style={{ marginTop:14, padding:'10px 14px', background:'#f0f6ff', borderRadius:8, fontSize:12.5, color:'#1a5fa8', lineHeight:1.55 }}>
                Book through your WhatsApp group. Examiner report sent within 24 hours.
              </div>
            </div>
            {/* Card 2: For Visitors */}
            <div style={{ background:'#fff', border:'1.5px solid #f0d890', borderRadius:14, padding:22 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#b8860b', background:'#fff8e8', border:'1px solid #f0d890', borderRadius:100, padding:'2px 10px', display:'inline-block', marginBottom:10, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.08em' }}>For Visitors</div>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:19, fontWeight:700, color:'#1a2a4a', marginBottom:10 }}>Available for a small fee</div>
              {[
                'One-time mock speaking exam',
                'Full examiner report on WhatsApp',
                'Band scores for all 4 criteria',
                'Personalised improvement tips',
              ].map((f,i)=>(
                <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-start', marginBottom:6, fontSize:13, color:'#2a3a5a', lineHeight:1.55 }}>
                  <span style={{ color:'#b8860b', flexShrink:0 }}>✓</span>{f}
                </div>
              ))}
              <button onClick={()=>openWA('Hello Mr. Ahmed Salem, I would like to book an IELTS Speaking Mock Exam as a visitor. Please send me details about availability and fees.')}
                style={{ width:'100%', marginTop:16, background:'#25d366', color:'#fff', border:'none', padding:'12px 0', borderRadius:8, fontSize:13.5, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, boxShadow:'0 2px 8px rgba(37,211,102,0.3)' }}>
                <WAIcon size={15}/> Book Now
              </button>
            </div>
          </div>
        </div>

        {/* ── WRITING ASSESSMENT ── */}
        <div style={{ marginBottom:44 }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>✍️ IELTS Writing Assessment</div>
          <div style={{ fontSize:13.5, color:'#5a6a88', marginBottom:20 }}>
            Submit your Writing Task 2 essay and receive detailed feedback, band scores, and improvement tips from our certified IELTS coaches.
          </div>

          {/* Process steps */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom:20 }}>
            {[
              { n:'1', title:'Submit your essay', icon:'📤' },
              { n:'2', title:'Our coaches review and score', icon:'👩‍🏫' },
              { n:'3', title:'Full feedback to your WhatsApp', icon:'📱' },
            ].map((step, i) => (
              <div key={i} style={{ background:'#fff', border:'1px solid #dde3ed', borderRadius:10, padding:'16px 12px', textAlign:'center', position:'relative' }}>
                <div style={{ width:26, height:26, borderRadius:'50%', background:'#1a2a4a', color:'#fff', fontSize:13, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 8px' }}>{step.n}</div>
                <div style={{ fontSize:22, marginBottom:6 }}>{step.icon}</div>
                <div style={{ fontSize:12.5, color:'#2a3a5a', fontWeight:500, lineHeight:1.4 }}>{step.title}</div>
                {i < 2 && <div style={{ position:'absolute', right:-8, top:'50%', fontSize:16, color:'#b8860b', transform:'translateY(-50%)', zIndex:1 }}>→</div>}
              </div>
            ))}
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:16 }}>
            {/* For IELTS Students */}
            <div style={{ background:'#fff', border:'1.5px solid #c0d8f8', borderRadius:14, padding:22, display:'flex', flexDirection:'column', justifyContent:'space-between' }}>
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:'#1a5fa8', background:'#e8f0fb', border:'1px solid #c0d8f8', borderRadius:100, padding:'2px 10px', display:'inline-block', marginBottom:10, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.08em' }}>For IELTS Students</div>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:19, fontWeight:700, color:'#1a2a4a', marginBottom:8 }}>Included in your package</div>
                <div style={{ fontSize:13.5, color:'#2a3a5a', lineHeight:1.6 }}>12 writing assessments per round — reviewed by our certified IELTS coaches with individual feedback and band scores.</div>
              </div>
              <div style={{ marginTop:14, padding:'10px 14px', background:'#f0f6ff', borderRadius:8, fontSize:12.5, color:'#1a5fa8' }}>Submit essays in your WhatsApp group.</div>
            </div>
            {/* For Visitors */}
            <div style={{ background:'#fff', border:'1.5px solid #f0d890', borderRadius:14, padding:22, display:'flex', flexDirection:'column', justifyContent:'space-between' }}>
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:'#b8860b', background:'#fff8e8', border:'1px solid #f0d890', borderRadius:100, padding:'2px 10px', display:'inline-block', marginBottom:10, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.08em' }}>For Visitors</div>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:19, fontWeight:700, color:'#1a2a4a', marginBottom:8 }}>One-time writing assessment</div>
                <div style={{ fontSize:13.5, color:'#2a3a5a', lineHeight:1.6 }}>Submit your Task 2 essay, receive band scores and improvement feedback from our coaches directly on WhatsApp.</div>
              </div>
              <button onClick={()=>openWA('Hello Mr. Ahmed Salem, I would like to submit my IELTS Writing Task 2 for assessment. Please send me details.')}
                style={{ width:'100%', marginTop:16, background:'#25d366', color:'#fff', border:'none', padding:'12px 0', borderRadius:8, fontSize:13.5, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, boxShadow:'0 2px 8px rgba(37,211,102,0.3)' }}>
                <WAIcon size={15}/> Book Now
              </button>
            </div>
          </div>
        </div>

        {/* ── CTA STRIP ── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(210px,1fr))', gap:12, marginBottom:36 }}>
          {[
            { icon:'📅', label:'Get Started',      sub:'Contact Mr. Ahmed to be matched',  onClick:()=>openWA('Hello Mr. Ahmed Salem! I would like to start lessons. Please help me get matched with a teacher.'), dark:true },
            { icon:'📝', label:'Placement Test',   sub:'Find your English level — free',   onClick:onPlacementTest, dark:false },
            { icon:'🎓', label:'Student Login',    sub:'Access your dashboard & classes',  onClick:onStudentLogin,  dark:false },
            { icon:'✏️', label:'Create Account',   sub:'New student? Register here',       onClick:onRegister,      dark:false },
          ].map(c => (
            <button key={c.label} onClick={c.onClick}
              style={{ background:c.dark?'linear-gradient(135deg,#1a2a4a,#2a3a6a)':'#fff', border:c.dark?'none':'1.5px solid #dde3ed', borderRadius:12, padding:'18px 16px', cursor:'pointer', textAlign:'left', boxShadow:c.dark?'0 4px 14px rgba(26,42,74,0.2)':'none' }}>
              <div style={{ fontSize:26, marginBottom:8 }}>{c.icon}</div>
              <div style={{ fontSize:14, fontWeight:700, color:c.dark?'#fff':'#1a2a4a', marginBottom:3 }}>{c.label}</div>
              <div style={{ fontSize:12, color:c.dark?'rgba(255,255,255,0.6)':'#5a6a88' }}>{c.sub}</div>
            </button>
          ))}
        </div>
      </div>

      {/* ── FOOTER ── */}
      <div style={{ background:'#1a2a4a', padding:'22px 24px', textAlign:'center' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10, marginBottom:8 }}>
          <div style={{ background:'rgba(255,255,255,0.1)', borderRadius:7, padding:'3px 7px', display:'flex', alignItems:'center' }}>
            <img src="/logo.png" alt="IELTS Compass" style={{ height:24, objectFit:'contain' }}/>
          </div>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:15, fontWeight:700, color:'#fff' }}>
            IELTS<span style={{ color:'#b8860b' }}>COMPASS</span>
          </div>
        </div>
        <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.1em' }}>
          wa.me/{WA_ADMIN}
        </div>
        <button onClick={onTeacherLogin} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.3)', fontSize:11, cursor:'pointer', marginTop:8, fontFamily:"'JetBrains Mono',monospace" }}>
          Teacher login
        </button>
      </div>
    </div>
  );
}
